import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { PromiseWatch } from "./lib/promise-helpers"

export type Room = {
  propertyid: number
  roomid: number
  sortorder: number
}
// private module logic //

const id: string = "room"
const initialState: ApiResult<Room[]> = { id, state: "init", value: [] }
const roomStream = new BehaviorSubject<ApiResult<Room[]>>(initialState)
const roomEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

let roomMap: {[key: number]: Room} = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  roomStream.next({ id, state: 'ok', value: [] })
  roomMap = {}
}

function get(roomid: number): Room {
  return roomMap[roomid]
}

function create(propertyid: number): Promise<Room | { message: string }> {
  const currentValue = roomStream.getValue().value
  roomStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    roomdetail: { state: 'basic' },
    occupancydetail: { state: 'basic' },
    adrdetail: { state: 'basic' },
    sortorder: 1
  }

  return Client.post(`room`, payload)
  .then((room: Room) => {
    push_undo({
      undofunc: () => deleteOne(propertyid, room.roomid),
      redofunc: () => create(propertyid)
    })
    roomMap[room.roomid] = room
    // Merge results unique by roomid & reorder
    const currentValue = roomStream.getValue().value
    let   newOrders    = currentValue.map(val => { return {...val, sortorder: val.sortorder + 1}});
    newOrders = newOrders.sort(function(a, b) {
      return a.sortorder - b.sortorder;
    });
    newOrders.unshift(room);
    reorder(propertyid, newOrders.map(val => val.roomid));
    roomStream.next({
      id,
      state: 'ok',
      value: _.unionBy([room], currentValue, 'roomid')
    })
    roomEventStream.next({ type: 'ROOM_CREATED', payload: room })
    return room
  })
  .catch((err: { message: string }) => {
    // on error overwrite optimistic update
    roomStream.next({ id, state: 'error', error: err, value: currentValue })
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    fetchRooms(propertyid)
    return err
  })
}

function fetchRooms (
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<Room[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  roomStream.next({ id, state: 'busy', value: roomStream.getValue().value })

  let url = `room?propertyid=eq.${propertyId}`
  if (limit && offset >= 0) {
    url = `room?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: Room[]) => {
    _.forEach(records, (rooms) => (roomMap[rooms.roomid] = rooms))
    // merge results unique by roomid
    roomStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function copy(propertyid:number, roomid:number, description:string) {
  let currentValue = roomStream.getValue().value;
  roomStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'room', recordid: roomid, _synchronous: true, codekey: 'description', unique: 'true'};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((room: Room) => {
      roomMap[room[0].roomid] = room[0]
      const sortorder = room[0].sortorder
      const currentValue = roomStream.getValue().value
      const value = _(currentValue).map((r) => (r.sortorder < sortorder ? r : { ...r, sortorder: r.sortorder + 1})).value()
      value.splice(sortorder - 1, 0, room[0])

      push_undo({
        undofunc: () => deleteOne(propertyid, room.roomid),
        redofunc: () => copy(propertyid, roomid, description)
      })

      roomStream.next({ id, state: 'ok', value })
      roomEventStream.next({ type: 'ROOM_CREATED', payload: room })
      notificationService.notify({
        type: 'info',
        message: 'Room copy complete.',
        dismiss: 3000
      })

      return room
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      roomStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRooms(propertyid)
      return err
    }
  )
}

function copies(propertyid:number, roomid:number, count: number) {
  let currentValue = roomStream.getValue().value;
  roomStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'room', recordid: roomid, _synchronous: true, codekey: 'description', unique: 'true', count};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((rooms: Room[]) => {
      notificationService.notify({
        type: 'info',
        message: 'Room copy complete.',
        dismiss: 3000
      })
      return fetchRooms(propertyid)
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      roomStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRooms(propertyid)
      return err
    }
  )
}

async function deleteOne(
  propertyid: number,
  roomid: number,
): Promise<boolean | { message: string }> {

  const currentValue = roomStream.getValue().value
  const sortorder = roomMap[roomid].sortorder

  // filter out the deleted multifamilyid and update sortorder
  const value = _(currentValue)
  .filter((r) => r.roomid !== roomid)
  .map((r) => (r.sortorder < sortorder ? r : { ...r, sortorder: r.sortorder - 1 }))
  .value()
  value.forEach((r) => (roomMap[r.roomid] = r))

  roomStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(roomid).then(() =>
  Client.del(`room?roomid=eq.${roomid}`)
    .then((room: Room) => {
      delete roomMap[room.roomid]
      reorder(propertyid, value.map(val => val.roomid));
      push_undo({
        undofunc: () => {
          Client.post('room', room)
          roomMap[room.roomid] = room
          roomStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, roomid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      roomStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRooms(propertyid)
      return err
    })
  )
}

async function deleteMultiple(
  propertyid: number,
  roomids: number[],
): Promise<boolean | { message: string }> {

  const currentValue = roomStream.getValue().value

  // filter out the deleted multifamilyid and update sortorder
  const value = _(currentValue)
    .filter((r) => !roomids.includes(r.roomid))
    .orderBy('sortorder')
    .map((p, ix) => ({...p, sortorder: ix+1}))
    .value()
  value.forEach((r) => (roomMap[r.roomid] = r))

  roomStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(roomids).then(() =>
  Client.del(`room?roomid=in.${roomids}`)
    .then((rooms: Room[]) => {
      rooms.forEach(r => delete roomMap[r.roomid])
      reorder(propertyid, value.map(val => val.roomid));
      push_undo({
        undofunc: () => {
          Client.post('room', rooms)
          rooms.forEach(r => roomMap[r.roomid] = r)
          roomStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteMultiple(propertyid, roomids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      roomStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRooms(propertyid)
      return err
    })
  )
}

function patch(room: Room): Promise<Room | { message: string }> {
  const currentValue = roomStream.getValue().value
  const roomid: number = room.roomid
  const currentRoom = _.find(currentValue, { roomid })
  const changeset = diff_obj(room, currentRoom)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentRoom),
    redofunc: () => patch(room)
  })

  // optimistic update
  roomStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {roomid}), room)
  })

  return _patches_.watch(roomid, Client.patch(`room?roomid=eq.${roomid}`, changeset)
    .then((room: Room[]) => {
      _.forEach(room, (room) => (roomMap[room.roomid] = room))
      return room[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      roomStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentRoom, 'propertyid')
      if (propertyid) fetchRooms(propertyid)
      return err
    }))
}

function move(
  propertyid: number,
  roomid: number,
  destination: number
): Promise<boolean | { message: string }> {
  if (!_.isFinite(destination)) return Promise.resolve(false)

  const currentValue = roomStream.getValue().value

  let undo
  const orders = (() => {
    let orders = _(currentValue).orderBy('sortorder').map('roomid').value()
    const current = _.findIndex(orders, (el) => el == roomid)
    undo = {
      undofunc: () => move(propertyid, roomid, current + 1),
      redofunc: () => move(propertyid, roomid, destination)
    }

    const dest = destination - 1
    if (dest < current) {
      orders.splice(dest, 0, roomid)
      orders.splice(current + 1, 1)
    } else {
      orders.splice(dest + 1, 0, roomid)
      orders.splice(current, 1)
    }
    return orders
  })()
  const value = _.map(orders, (id, ix) => {
    roomMap[id] = { ...roomMap[id], sortorder: ix + 1 }
    return roomMap[id]
  })
  roomStream.next({ id, state: 'ok', value })

  const payload = {
    table: 'room',
    propertyid,
    orders,
    _synchronous: true
  }

  // update the db
  return Client.post(`msg/svc_reorder`, payload)
  .then((room: Room) => {
    // update already happened optimistically
    push_undo(undo)
    return true
  })
  .catch((err: { message: string }) => {
    // on error overwrite optimistic update
    roomStream.next({ id, state: 'error', error: err, value: currentValue })
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    fetchRooms(propertyid)
    return err
  })
}

function reorder(propertyid:number, orders:number[]){
  const currentValue = roomStream.getValue().value
  const payload      = {table: 'room', propertyid, orders, _synchronous: true};

  return Client.post(`msg/svc_reorder`, payload)
  .then((room: Room) => {
    // fetchRooms(propertyid)
  })
  .catch((err: { message: string }) => {
    // on error overwrite optimistic update
    roomStream.next({ id, state: 'error', error: err, value: currentValue })
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    fetchRooms(propertyid)
    return err
  })
}

// public module interface //

const roomService = {
  stream: () => roomStream.asObservable(),
  value: () => roomStream.getValue().value,
  eventStream: () => roomEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  deleteMultiple,
  get,
  fetchRooms,
  move,
  patch
}

export default roomService
